import React from "react";
import PropTypes from "prop-types";
import {
  Grid,
  Statistic,
  Segment,
  Header,
  Icon,
  Table,
  Loader,
  Label,
} from "semantic-ui-react";
import TrendAnalysisChart from "./TrendAnalysisChart";

// Constants
const CARD_STYLE = {
  textAlign: "center",
  padding: "2rem",
  height: "220px",
  display: "flex",
  flexDirection: "column",
  justifyContent: "center",
};

const STATISTIC_VALUE_STYLE = {
  fontSize: "3rem",
};

const STATISTIC_LABEL_STYLE = {
  color: "#666",
  fontSize: "1rem",
};

const SUBTITLE_STYLE = {
  color: "#999",
  fontSize: "0.8rem",
  marginTop: "0.5rem",
};

const HEADER_STYLE = {
  color: "#225745",
};

const METRIC_CARDS = [
  {
    key: "activeDays",
    icon: "calendar alternate",
    iconColor: "#2185d0",
    valueColor: "#2185d0",
    label: "Active Days",
    getValue: (metrics, searchTrends) => metrics.activeDays,
    getSubtitle: (metrics, searchTrends) => `OF ${searchTrends.length}`,
  },
  {
    key: "avgSearchesPerDay",
    icon: "chart line",
    iconColor: "#00b5ad",
    valueColor: "#00b5ad",
    label: "Daily Average",
    getValue: (metrics) => metrics.avgSearchesPerDay,
    getSubtitle: () => "SEARCHES/DAY",
  },
  {
    key: "peakDay",
    icon: "trophy",
    iconColor: "#f2711c",
    valueColor: "#f2711c",
    label: "Peak Day",
    getValue: (metrics) => metrics.peakDay.total,
    getSubtitle: (metrics) =>
      new Date(metrics.peakDay.date)
        .toLocaleDateString("en-US", { month: "short", day: "numeric" })
        .toUpperCase(),
  },
  {
    key: "usageRate",
    icon: "percent",
    iconColor: "#21ba45",
    valueColor: "#21ba45",
    label: "Usage Rate",
    getValue: (metrics) => `${metrics.usageRate}%`,
    getSubtitle: () => "ACTIVE DAYS",
  },
];

const Overview = ({ overviewStats, searchTrends, statsLoading }) => {
  const calculateUsageMetrics = () => {
    if (!searchTrends?.length) return null;

    const totalSearches = searchTrends.reduce(
      (sum, item) => sum + item.total,
      0
    );
    const avgSearchesPerDay = Math.round(totalSearches / searchTrends.length);
    const peakDay = searchTrends.reduce(
      (max, item) => (item.total > max.total ? item : max),
      searchTrends[0]
    );
    const activeDays = searchTrends.filter((item) => item.total > 0).length;
    const recentData = searchTrends.slice(-7);
    const recentAvg = Math.round(
      recentData.reduce((sum, item) => sum + item.total, 0) / recentData.length
    );

    return {
      totalSearches,
      avgSearchesPerDay,
      peakDay,
      activeDays,
      usageRate: Math.round((activeDays / searchTrends.length) * 100),
      recentAvg,
    };
  };

  const getTrendDirection = (usageMetrics) => {
    const recentData = searchTrends.slice(-7);
    const olderData = searchTrends.slice(0, -7);
    const recentAvg =
      recentData.reduce((sum, item) => sum + item.total, 0) / recentData.length;
    const olderAvg =
      olderData.length > 0
        ? olderData.reduce((sum, item) => sum + item.total, 0) /
          olderData.length
        : 0;

    return recentAvg > olderAvg
      ? "up"
      : recentAvg < olderAvg
      ? "down"
      : "stable";
  };

  const getTrendIcon = (direction) => {
    const iconMap = {
      up: { name: "arrow up", color: "green", text: "Increasing Usage" },
      down: { name: "arrow down", color: "red", text: "Decreasing Usage" },
      stable: { name: "minus", color: "grey", text: "Stable Usage" },
    };
    return iconMap[direction];
  };

  const renderMetricCard = (config, usageMetrics) => (
    <Grid.Column key={config.key}>
      <Segment style={CARD_STYLE}>
        <Icon
          name={config.icon}
          size="big"
          style={{ color: config.iconColor, marginBottom: "1rem" }}
        />
        <Statistic>
          <Statistic.Value
            style={{ ...STATISTIC_VALUE_STYLE, color: config.valueColor }}
          >
            {config.getValue(usageMetrics, searchTrends)}
          </Statistic.Value>
          <Statistic.Label style={STATISTIC_LABEL_STYLE}>
            {config.label}
          </Statistic.Label>
        </Statistic>
        <div style={SUBTITLE_STYLE}>
          {config.getSubtitle(usageMetrics, searchTrends)}
        </div>
      </Segment>
    </Grid.Column>
  );

  if (statsLoading) {
    return (
      <Loader active inline="centered">
        Loading statistics...
      </Loader>
    );
  }

  const usageMetrics = calculateUsageMetrics();
  const trendDirection = usageMetrics
    ? getTrendDirection(usageMetrics)
    : "stable";
  const trendIcon = getTrendIcon(trendDirection);

  return (
    <div>
      <Grid stackable>
        {/* Statistics Overview */}
        <Grid.Row>
          <Grid.Column width={16}>
            <Grid>
              <Grid.Row columns={5}>
                {/* Total Searches Card */}
                <Grid.Column>
                  <Segment style={CARD_STYLE}>
                    <Icon
                      name="search"
                      size="big"
                      style={{ color: "#4a90e2", marginBottom: "1rem" }}
                    />
                    <Statistic>
                      <Statistic.Value
                        style={{ ...STATISTIC_VALUE_STYLE, color: "#f4b952" }}
                      >
                        {overviewStats.totalSearches.toLocaleString()}
                      </Statistic.Value>
                      <Statistic.Label style={STATISTIC_LABEL_STYLE}>
                        Total Searches
                      </Statistic.Label>
                    </Statistic>
                  </Segment>
                </Grid.Column>

                {/* Dynamic Metric Cards */}
                {usageMetrics &&
                  METRIC_CARDS.map((config) =>
                    renderMetricCard(config, usageMetrics)
                  )}
              </Grid.Row>
            </Grid>
          </Grid.Column>
        </Grid.Row>

        {/* Search Trends Chart */}
        <Grid.Row>
          <Grid.Column width={16}>
            <TrendAnalysisChart searchTrends={searchTrends} />
          </Grid.Column>
        </Grid.Row>

        {/* Usage Insights */}
        {usageMetrics && (
          <Grid.Row>
            <Grid.Column width={16}>
              <Grid>
                <Grid.Row>
                  <Grid.Column width={8}>
                    <Segment>
                      <Header as="h4" style={HEADER_STYLE}>
                        <Icon name="chart bar" />
                        Usage Summary
                      </Header>
                      <Table basic>
                        <Table.Body>
                          <Table.Row>
                            <Table.Cell width={8}>
                              <Label color="teal" size="small">
                                Total Period Searches
                              </Label>
                            </Table.Cell>
                            <Table.Cell width={8}>
                              <strong>{usageMetrics.totalSearches}</strong>
                            </Table.Cell>
                          </Table.Row>
                          <Table.Row>
                            <Table.Cell>
                              <Label color="blue" size="small">
                                Average Response Time
                              </Label>
                            </Table.Cell>
                            <Table.Cell>
                              <strong>
                                {overviewStats.avgResponseTime.toFixed(2)}ms
                              </strong>
                            </Table.Cell>
                          </Table.Row>
                          <Table.Row>
                            <Table.Cell>
                              <Label color="green" size="small">
                                System Usage Rate
                              </Label>
                            </Table.Cell>
                            <Table.Cell>
                              <strong>{usageMetrics.usageRate}%</strong>
                            </Table.Cell>
                          </Table.Row>
                        </Table.Body>
                      </Table>
                    </Segment>
                  </Grid.Column>

                  <Grid.Column width={8}>
                    <Segment>
                      <Header as="h4" style={HEADER_STYLE}>
                        <Icon name="arrow up" /> Trend Analysis
                      </Header>
                      <div style={{ textAlign: "center", padding: "1rem" }}>
                        <Icon
                          name={trendIcon.name}
                          size="large"
                          color={trendIcon.color}
                        />
                        <div style={{ marginTop: "0.5rem" }}>
                          <strong>{trendIcon.text}</strong>
                        </div>
                        <div
                          style={{
                            fontSize: "0.9em",
                            color: "#666",
                            marginTop: "0.5rem",
                          }}
                        >
                          Recent average: {usageMetrics.recentAvg.toFixed(1)}{" "}
                          searches/day
                        </div>
                      </div>
                    </Segment>
                  </Grid.Column>
                </Grid.Row>
              </Grid>
            </Grid.Column>
          </Grid.Row>
        )}
      </Grid>
    </div>
  );
};

Overview.displayName = "Overview";

Overview.propTypes = {
  overviewStats: PropTypes.shape({
    totalSearches: PropTypes.number.isRequired,
    avgResponseTime: PropTypes.number.isRequired,
  }).isRequired,
  searchTrends: PropTypes.arrayOf(
    PropTypes.shape({
      date: PropTypes.string.isRequired,
      total: PropTypes.number.isRequired,
    })
  ).isRequired,
  statsLoading: PropTypes.bool.isRequired,
};

export default Overview;
